import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Bookmark,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Filter,
  Globe,
  Search,
  User,
} from "lucide-react";
import { api, type ArticleFilter, type ArticleRowView, type Language } from "../../lib/api.js";
import { StatCard } from "../../components/ui/StatCard.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { EmptyState } from "../../components/ui/EmptyState.js";

/**
 * Articles catalogue. Distinct from the Pipeline (which is a workflow board) —
 * this is the PUBLICATION catalogue. Filter, search, sort, preview.
 *
 * Clicking a row deep-links into Pipeline?id=... which auto-opens the detail
 * panel with full article markdown + actions (refresh, distribute, etc).
 */
export function ArticlesPage() {
  const [sort, setSort] = useState<NonNullable<ArticleFilter["sort"]>>("newest");
  const [language, setLanguage] = useState<Language | "">("");
  const [search, setSearch] = useState("");

  const { data, isPending, error } = useQuery({
    queryKey: ["articles", { sort, language, q: search }],
    queryFn: () =>
      api.listArticles({
        sort,
        language: language || undefined,
        q: search || undefined,
      }),
    refetchInterval: 60_000,
  });

  if (error) {
    return (
      <div className="card p-6 text-sm text-danger">
        Failed to load articles: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-ink">Articles</h2>
        <p className="mt-1 text-sm text-muted">
          Every produced article in one place. Click a row to open it in Pipeline for preview, refresh, and distribution.
        </p>
      </div>

      {/* KPI strip */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total articles"
          value={isPending ? "—" : data?.stats.totalArticles ?? 0}
          icon={FileText}
          tone="brand"
          hint={data ? `${data.stats.hebrew} HE · ${data.stats.english} EN` : undefined}
        />
        <StatCard
          label="Pillar pages"
          value={isPending ? "—" : data?.stats.pillars ?? 0}
          icon={Bookmark}
          tone="accent"
          hint="Topic-hub articles"
        />
        <StatCard
          label="Avg word count"
          value={isPending ? "—" : data?.stats.avgWordCount.toLocaleString() ?? 0}
          icon={Globe}
          tone="neutral"
          hint="across published"
        />
        <StatCard
          label="Total Claude spend"
          value={isPending ? "—" : `$${data?.stats.totalCostUsd.toFixed(2) ?? "0.00"}`}
          icon={DollarSign}
          tone="warn"
          hint="article production only"
        />
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, keyword, cluster…"
            className="input w-72 pl-9"
          />
        </div>

        <FilterDropdown
          icon={Globe}
          label="Language"
          value={language || "All"}
          options={[
            { value: "", label: "All" },
            { value: "he", label: "Hebrew" },
            { value: "en", label: "English" },
          ]}
          onChange={(v) => setLanguage(v as Language | "")}
        />

        <FilterDropdown
          icon={Filter}
          label="Sort"
          value={SORT_LABEL[sort]}
          options={[
            { value: "newest",   label: "Newest" },
            { value: "oldest",   label: "Oldest" },
            { value: "stalest",  label: "Stalest (needs refresh)" },
            { value: "longest",  label: "Longest" },
            { value: "shortest", label: "Shortest" },
          ]}
          onChange={(v) => setSort(v as NonNullable<ArticleFilter["sort"]>)}
        />

        <div className="ml-auto text-xs text-muted">
          {data ? `${data.count} article${data.count === 1 ? "" : "s"}` : ""}
        </div>
      </div>

      {/* Table */}
      <SectionCard
        title="Catalogue"
        icon={FileText}
        padded={false}
      >
        {isPending ? (
          <ul className="divide-y divide-line">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="px-5 py-4">
                <Skeleton className="mb-2 h-4 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </li>
            ))}
          </ul>
        ) : data && data.articles.length > 0 ? (
          <ArticleTable rows={data.articles} />
        ) : (
          <EmptyState
            icon={FileText}
            title="No articles match these filters"
            description="Adjust the language or search query, or produce a new article from the Pipeline."
          />
        )}
      </SectionCard>
    </div>
  );
}

const SORT_LABEL: Record<NonNullable<ArticleFilter["sort"]>, string> = {
  newest: "Newest",
  oldest: "Oldest",
  stalest: "Stalest",
  longest: "Longest",
  shortest: "Shortest",
};

function ArticleTable({ rows }: { rows: ArticleRowView[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-canvas/40 text-2xs font-semibold uppercase tracking-wider text-muted">
            <th className="px-5 py-2.5 text-left font-semibold">Title</th>
            <th className="px-3 py-2.5 text-left font-semibold">Cluster</th>
            <th className="px-3 py-2.5 text-right font-semibold">Words</th>
            <th className="px-3 py-2.5 text-right font-semibold">Cost</th>
            <th className="px-3 py-2.5 text-left font-semibold">Last reviewed</th>
            <th className="px-3 py-2.5 text-left font-semibold">Signals</th>
            <th className="px-5 py-2.5 text-right" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ArticleRow key={row.keywordId} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArticleRow({ row }: { row: ArticleRowView }) {
  const open = () => {
    window.location.hash = `/pipeline?open=${encodeURIComponent(row.keywordId)}`;
  };
  return (
    <tr
      onClick={open}
      className="cursor-pointer border-b border-line transition last:border-b-0 hover:bg-canvas/60"
    >
      <td className="px-5 py-3 align-top">
        <div className="flex items-start gap-2">
          {row.pillarPage && (
            <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" title="Pillar page" />
          )}
          <div className="min-w-0">
            <div className="truncate font-medium text-ink" dir={row.language === "he" ? "rtl" : "ltr"}>
              {row.h1}
            </div>
            <div className="truncate text-2xs text-muted">
              <span dir={row.language === "he" ? "rtl" : "ltr"}>{row.keyword}</span>
              {row.slug && <span className="ml-2 font-mono">/{row.slug}</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 align-top text-xs text-muted">{row.cluster ?? "—"}</td>
      <td className="px-3 py-3 text-right align-top text-xs font-mono text-ink">{row.totalWordCount.toLocaleString()}</td>
      <td className="px-3 py-3 text-right align-top text-xs font-mono text-muted">
        {row.estimatedCostUsd !== null ? `$${row.estimatedCostUsd.toFixed(4)}` : "—"}
      </td>
      <td className="px-3 py-3 align-top text-xs text-muted">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {relativeTime(row.lastReviewedAt ?? row.generatedAt)}
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <div className="flex flex-wrap items-center gap-1">
          <LangChip language={row.language} />
          {row.hasAuthor && <SignalChip icon={User} label="Byline" />}
          {row.hasRelatedLinks && <SignalChip icon={ArrowUpRight} label="Related" />}
        </div>
      </td>
      <td className="px-5 py-3 text-right align-top">
        <button onClick={open} className="btn-ghost btn-sm">
          Open
          <ExternalLink className="h-3 w-3" />
        </button>
      </td>
    </tr>
  );
}

function LangChip({ language }: { language: Language }) {
  return (
    <span className="inline-flex items-center rounded-full bg-line-soft px-1.5 py-0.5 text-2xs font-medium uppercase text-muted-dark">
      {language}
    </span>
  );
}

function SignalChip({ icon: Icon, label }: { icon: typeof User; label: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-brand-50 px-1.5 py-0.5 text-2xs font-medium text-brand-700">
      <Icon className="h-3 w-3" strokeWidth={2.2} />
      {label}
    </span>
  );
}

function FilterDropdown({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: typeof User;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="relative inline-flex items-center gap-2 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs">
      <Icon className="h-3.5 w-3.5 text-muted" />
      <span className="text-muted">{label}:</span>
      <select
        value={options.find((o) => o.label === value)?.value ?? options[0]?.value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer bg-transparent text-ink focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" });
}

// Silence unused-import — useMemo retained for future filter memoisation.
const _useMemoSilencer = useMemo;
void _useMemoSilencer;
